import * as BizCharts from 'bizcharts';
import React, { FC } from 'react';
import { STOPLIGHT_COLORS } from '../../utils/DataFormatterUtils';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import moment from 'moment';
import { isBetweenMoments } from '../../utils/AuthUtils';
import './chartTheme';
import { withNiceScale } from './chartUtils';

const { Chart, Axis, Geom, Tooltip, Annotation, View, Legend } = BizCharts;

interface LineChartProps {
	cols: any;
	height: number;
	data: any;
	fillArea: boolean;
	position: string;
	chartPadding?: [number, number, number, number];
	color?: string;
	color2?: string;
	xAxisName: string;
	yAxisName: string;
	xAxisLabel?: any;
	yAxisLabel?: any;
	tooltipTemplate?: any;
	currency: any;
	budget: any;
}

const getBudgetTotalForElapsedPeriod = (cycleStartDate, cycleEndDate, total) => {
	if (cycleStartDate && cycleEndDate) {
		if (isBetweenMoments(cycleStartDate, cycleEndDate)) {
			const cycleDays = moment(cycleEndDate).diff(moment(cycleStartDate), 'days');
			const currentCycleDays = moment().diff(moment(cycleStartDate), 'days');
			return (currentCycleDays / cycleDays) * parseFloat(total);
		}
	}
	return total;
};

export const BudgetDetailChart: FC<LineChartProps> = ({
	chartPadding,
	tooltipTemplate,
	data,
	cols,
	color,
	color2,
	height,
	fillArea,
	xAxisName,
	yAxisName,
	xAxisLabel,
	yAxisLabel,
	position,
	currency,
	budget,
}) => {
	const budgetTotalForElapsedPeriod = getBudgetTotalForElapsedPeriod(
		nullSafeGet('cycleStartDate', budget),
		nullSafeGet('cycleEndDate', budget),
		budget.budget
	);
	const firstDayKey = data && data.length > 0 ? data[0][xAxisName] : null;
	const lastDayKey = data && data.length > 0 ? data[data.length - 1][xAxisName] : null;
	return (
		<Chart
			height={height}
			padding="auto"
			scale={withNiceScale(cols)}
			autoFit={true}
			data={data}
			theme="openwrench"
		>
			<Axis name={xAxisName} label={xAxisLabel} min={0} />
			<Axis name={yAxisName} label={yAxisLabel} />
			<Tooltip
				itemTpl={
					tooltipTemplate
						? tooltipTemplate
						: `
						<li data-index={index}>
							<!-- The marker for each record -->
							{value}
						</li>
					`
				}
			/>
			<Geom
				type="line"
				position={position}
				size={2}
				color={'black'}
				tooltip={[
					'dayKey*totalConsumedAmount',
					(dayKey, totalConsumedAmount) => {
						return {
							value: currency.format(totalConsumedAmount),
						};
					},
				]}
			/>
			<Geom
				type="point"
				position={position}
				size={4}
				shape={'circle'}
				color={'black'}
				style={{ stroke: '#fff', lineWidth: 1 }}
			/>

			{firstDayKey && lastDayKey && (
				<Annotation.Line
					top={false}
					start={[firstDayKey, 0]}
					end={[lastDayKey, budgetTotalForElapsedPeriod]}
					style={{
						stroke: '#999',
						lineDash: [2, 2],
						lineWidth: 1,
					}}
					text={{
						position: 'start',
						autoRotate: true,
						content: 'Expected Budget Spent',
						offsetX: 64,
						offsetY: -8,
					}}
				/>
			)}
		</Chart>
	);
};
